// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Domain glossary + inline ℹ popovers. `gloss('lowband','long-reach')` renders a
// clickable term; initGlossary() wires one fixed popover (click-away / Escape to
// dismiss). Assume the reader has never thought about radio spectrum.

import { attr, esc } from './format';

export const GLOSSARY: Record<string, { term: string; def: string }> = {
  site: {
    term: 'Mobile site',
    def: 'One registered location where a carrier has mobile equipment — a tower, a rooftop mast, a monopole outside a shop. Carriers register each one with a national site number (RFNSA), and this site treats one carrier at one registered number as one "tower". Several separate installations can share a registered site; where that happens their equipment lists are combined.',
  },
  band: {
    term: 'Frequency band',
    def: 'The slice of radio spectrum a piece of equipment transmits on, named for its rough frequency — LTE700 is 4G on 700 MHz, NR3600 is 5G on 3,600 MHz. A tower usually runs several bands at once, and which ones it runs determines both how far it reaches and how fast it is.',
  },
  lowband: {
    term: 'Long-reach spectrum (under 1 GHz)',
    def: 'Radio below 1,000 MHz — the 700, 850 and 900 MHz bands. Low frequencies travel much further and pass through walls, hills and trees far better than high ones, so they are what actually delivers coverage across the countryside. A single low-band tower can serve tens of kilometres; without it, the same tower might reach only a few.',
  },
  midband: {
    term: 'Capacity spectrum (1.8–3.6 GHz)',
    def: 'The middle of the spectrum. It carries far more data than low-band, which is what makes a connection fast, but it fades quickly with distance and struggles to get inside buildings. It adds speed where people already are — it does not extend the edge of the network.',
  },
  mmwave: {
    term: 'Millimetre wave (26 GHz)',
    def: 'Extremely high frequency, extremely fast, and effectively stopped by a wall or a rain shower. Used only for small hotspots in dense places such as stadiums and city centres.',
  },
  gen: {
    term: 'Generation (3G, 4G, 5G)',
    def: 'The technology a band runs. 3G is the generation Australia switched off between 2024 and 2025; 4G (LTE) still does most of the work; 5G (NR) is newest. Generation and reach are independent: 5G on 700 MHz reaches a long way, while 5G on 3,600 MHz does not.',
  },
  shutdown: {
    term: 'The 3G shutdown',
    def: 'Australia\'s carriers closed their 3G networks between 2024 and 2025 and re-used the spectrum for 4G and 5G. Where a tower\'s 3G ran on a long-reach band and the replacement did not, that tower\'s usable range physically shrank even though the tower is still standing.',
  },
  iot: {
    term: 'NB-IoT',
    def: 'Narrowband Internet of Things — a low-power, very low-speed service for machines rather than phones: water meters, farm sensors, tracking tags. It shares a tower with the normal network but does not carry calls or browsing.',
  },
  mocn: {
    term: 'Network sharing (MOCN)',
    def: 'A Multi-Operator Core Network: two carriers run their customers over one shared set of radio equipment. Optus and TPG do this at thousands of sites, so a TPG customer can be served by equipment TPG does not own. It changes who covers you without changing who owns the tower.',
  },
  cofunded: {
    term: 'Co-funded site',
    def: 'A tower the carrier did not pay for on its own — the public contributed, through the federal Mobile Black Spot Program or a state scheme. The carrier still owns and operates it, and only its own customers get service from it.',
  },
  mbsp: {
    term: 'Mobile Black Spot Program',
    def: 'The federal program that pays carriers to build towers in places where poor coverage would otherwise never justify the cost. It runs in numbered rounds, and each funded site in this data carries its program and round.',
  },
  sa3: {
    term: 'SA3 region',
    def: 'A Statistical Area Level 3 — the Australian Bureau of Statistics region used here, roughly the size of a large council area or a group of suburbs. There are 340 of them with boundaries. They are big: a region can look well covered while parts of it have no signal at all.',
  },
  realoption: {
    term: 'A real option',
    def: 'How this site decides whether a carrier genuinely serves a region. Because an SA3 is large, nearly every one has all three carriers somewhere inside it, which would make everywhere look competitive. So a carrier counts as a real option only where its long-reach site count is at least a third of the best-served carrier\'s. The raw per-carrier counts are always shown on hover so you can judge the call yourself.',
  },
  additionality: {
    term: 'Additionality',
    def: 'Whether public money bought something that would not otherwise exist. A funded tower far from any competing network filled a genuine gap; one built beside a rival\'s existing tower did not extend coverage to anybody new, it added a second network in a place that already had one.',
  },
  capture: {
    term: 'Capture ratio',
    def: 'A carrier\'s share of publicly funded towers divided by its share of the towers it built commercially. A ratio of 1 means it won public money in proportion to its size. Above 1 means it won more than its size implies; below 1, less.',
  },
  rfnsa: {
    term: 'RFNSA site number',
    def: 'The national Radio Frequency National Site Archive number that identifies a registered mobile site. It is how a tower is tracked from one year to the next. The 2018 and 2019 files do not carry it, which is why per-tower history on this site begins in 2020.',
  },
  erp: {
    term: 'Estimated Resident Population',
    def: 'The Australian Bureau of Statistics\' official population count for a region, used here as the denominator wherever a figure is expressed per resident.',
  },
};

export function gloss(key: string, label?: string): string {
  const g = GLOSSARY[key];
  if (!g) return esc(label ?? key);
  return `<span class="glossary-link" data-term="${attr(key)}" role="button" tabindex="0" aria-label="${attr(g.term)} — definition">${esc(label ?? g.term)}<span class="gl-i" aria-hidden="true">ℹ</span></span>`;
}

let popEl: HTMLDivElement | null = null;

function ensurePop(): HTMLDivElement {
  if (!popEl) {
    popEl = document.createElement('div');
    popEl.className = 'gloss-pop';
    popEl.setAttribute('role', 'dialog');
    document.body.appendChild(popEl);
  }
  return popEl;
}

function show(target: HTMLElement, key: string): void {
  const g = GLOSSARY[key];
  if (!g) return;
  const el = ensurePop();
  el.innerHTML = `<strong>${esc(g.term)}</strong><p>${esc(g.def)}</p>`;
  el.classList.add('visible');
  const r = target.getBoundingClientRect();
  const pw = Math.min(340, window.innerWidth - 24);
  el.style.width = `${pw}px`;
  let left = r.left;
  if (left + pw + 12 > window.innerWidth) left = window.innerWidth - pw - 12;
  el.style.left = `${Math.max(12, left)}px`;
  const below = r.bottom + 8;
  const hh = el.offsetHeight;
  el.style.top = below + hh + 12 > window.innerHeight && r.top - hh - 8 > 8 ? `${r.top - hh - 8}px` : `${below}px`;
}

export function hideGloss(): void {
  if (popEl) popEl.classList.remove('visible');
}

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest?.('.glossary-link') as HTMLElement | null;
    if (link) {
      e.stopPropagation();
      show(link, link.getAttribute('data-term') || '');
      return;
    }
    if (popEl && !(e.target as Element).closest?.('.gloss-pop')) hideGloss();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideGloss();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element).classList?.contains('glossary-link')) {
      e.preventDefault();
      show(e.target as HTMLElement, (e.target as HTMLElement).getAttribute('data-term') || '');
    }
  });
  window.addEventListener('scroll', hideGloss, true);
}
